import { useEffect, useMemo, useCallback } from "react";
import PageContent from "components/PageContent";
import Loader from "../../Loader/Loader";
import Constants from "../../../config/Constants";
import AthleteForm from "../../Forms/AthleteForm";
import useAthleteDeleteEmailMutation from "../hooks/useAthleteDeleteEmailMutation";
import useAthleteUpdateEmailMutation from "../hooks/useAthleteUpdateEmailMutation";
import Grid from "components/primitives/Grid";
import Box from "@mui/material/Box";
import useGroupsContext from "hooks/useGroupsContext";
import { useInfoMessage, useLoadingContext, useRequest } from "hooks";
import useAllAthletes from "hooks/useAllAthletes";
import useAthleteActions from "providers/AthletesProvider/useAthleteActions";
import { useMatch, useNavigate } from "react-router-dom";
import { style } from "config";
import Mui5 from "components/mui5";
import ArchivedAthleteAlert from "./ArchivedAthleteAlert";
import useOrganisation from "contexts/OrganisationContext/useOrganisation";
import CanDoAction from "components/CanDoAction";
import useCanDo from "hooks/useCanDo";
import useOrganisationActions from "providers/OrganisationProvider/useOrganisationActions";
import useAthleteProfilePhotoURL from "hooks/useAthleteProfilePhotoURL";
import AthleteHelpers from "helpers/AthleteHelpers";
import { useTranslation } from "react-i18next";
import useAthleteLimits from "hooks/useAthleteLimits";
import Tooltip from "components/Tooltip";
import AthleteArchiveDisabledAlert from "../components/AthleteArchiveDisabledAlert";
import DeleteAthleteAction from "./DeleteAthleteAction";
import ArchiveAthleteAction from "./ArchiveAthleteAction";
import ErrorLoggingService from "services/ErrorLoggingService";

function EditAthlete() {
  const canDo = useCanDo();
  const onError = useInfoMessage({ type: "error" });
  const athletes = useAllAthletes();
  const athleteActions = useAthleteActions();
  const [groups, groupActions] = useGroupsContext();
  const handleUpdateAthlete = useRequest(athleteActions.update, true);
  const handleArchiveAthlete = useRequest(athleteActions.toggleArchived, true);
  const [loading] = useLoadingContext();
  const match = useMatch("/athlete-hub/:id/edit");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const athleteLimits = useAthleteLimits();

  const infoSuccess = useInfoMessage({ type: "success" });

  // TODO: (0S-4710) Replace with AccountInfoProvider
  const organisation = useOrganisation();
  const organisationActions = useOrganisationActions();
  // END TODO

  const athleteId = match.params.id;

  const { trigger: updateEmailTrigger } = useAthleteUpdateEmailMutation({ athleteId: athleteId });
  const { trigger: deleteEmailTrigger } = useAthleteDeleteEmailMutation({ athleteId: athleteId });

  if (typeof athleteId === "undefined") {
    throw new Error("No Athlete ID provided");
  }

  useEffect(() => {
    groupActions.fetchAll();
    // TODO: (0S-4710) Replace with AccountInfoProvider
    organisationActions.get();
    // END TODO
  }, [groupActions, organisationActions]);

  const { groupIds = [], ...athlete } = useMemo(() => {
    const athlete = athletes.find((athlete) => athlete.id === athleteId);

    if (!athlete) return {};

    return {
      externalId: athlete?.externalId || "",
      email: athlete?.email || "",
      ...athlete,
    };
  }, [athleteId, athletes]);

  /** The address the athlete has, which the form seeds its email field with: pending change first, then confirmed. */
  const athleteEmail = useMemo(
    () => AthleteHelpers.getAthleteEmail({ email: athlete.email ?? null, pendingEmail: athlete.pendingEmail ?? null }),
    [athlete.email, athlete.pendingEmail]
  );

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const existingGroupIds = groups
          .filter((group) =>
            values.groups.find(
              (g) => g.id === group.id || (g?.toLowerCase && g.toLowerCase()) === group.name.toLowerCase()
            )
          )
          .map((group) => group.id);

        const newGroupNames = values.groups
          .filter(
            (g) =>
              typeof g === "string" &&
              Boolean(g) &&
              !groups.find((group) => group.name.toLowerCase() === g.toLowerCase())
          )
          .filter(
            (groupName, index, arr) =>
              !arr.slice(0, index).find((newGroupName) => newGroupName.toLowerCase() === groupName.toLowerCase())
          );

        let newGroups = [];

        if (canDo("group.any")) {
          newGroups = await Promise.all(
            newGroupNames.map((name) => {
              const randomColour = style.colors.options[Math.floor(Math.random() * style.colors.options.length)];
              return groupActions.create({ name, colour: randomColour, athleteIds: [], description: "" });
            })
          );
        }

        const newGroupIds = newGroups.map((group) => group.id);

        await handleUpdateAthlete({
          id: athleteId,
          firstName: values.firstName,
          lastName: values.lastName,
          externalId: values.externalId,
          dateOfBirth: values.dateOfBirth,
          weight: values.weight,
          height: values.height,
          sex: values.sex,
          profilePhotoName: values.profilePhotoName,
          groupIds: organisation.featureConfig.isGroupsEnabled ? existingGroupIds.concat(newGroupIds) : groupIds,
        });

        /**
         * We handle in a different flow from the updateAthlete call to ensure that the email changes are processed correctly and to provide appropriate feedback to the user.
         * Even though they are triggered as part of the same form submission, they are separate operations that may have different outcomes (success or failure) and need to be handled independently.
         */

        /** The form seeds `email` with the address the athlete has, so it differs only when the coach edited it. */
        const hasEditedEmail = (values.email ?? "") !== athleteEmail;

        if (hasEditedEmail && !values.email) {
          await deleteEmailTrigger(
            { athleteId: athlete.id },
            {
              onSuccess: () => {
                infoSuccess({
                  message: athlete.email
                    ? t("athlete.email_has_been_removed_successfully")
                    : t("athlete.invite_cancel_success"),
                });
              },
            }
          );
        } else if (hasEditedEmail) {
          await updateEmailTrigger(
            { email: values.email, athleteId: athlete.id },
            {
              onSuccess: () => {
                /** Restoring the confirmed address drops the pending change rather than inviting a new address. */
                if (values.email === athlete.email) return;
                infoSuccess({
                  message: t("athlete.invite_sent_success"),
                });
              },
            }
          );
        }

        navigate(`${Constants.routes.athletes.route}/${athleteId}`);
      } catch (error) {
        ErrorLoggingService.captureException(error, "Error updating athlete");
        onError({ message: t("errors.generic_message") });
      }
    },
    [
      groups,
      athleteId,
      organisation.featureConfig.isGroupsEnabled,
      groupIds,
      athlete.email,
      athlete.id,
      athleteEmail,
      groupActions,
      canDo,
      handleUpdateAthlete,
      navigate,
      updateEmailTrigger,
      infoSuccess,
      t,
      deleteEmailTrigger,
      onError,
    ]
  );

  const disableArchiveButton = athleteLimits.archived.isFull;

  const allowMaxTwoDecimals = useCallback((value) => Math.round((value + Number.EPSILON) * 100) / 100, []);

  const avatar = useAthleteProfilePhotoURL(athlete);

  const handleToggleArchived = useCallback(async () => {
    await handleArchiveAthlete(athleteId, !athlete.isArchived);
  }, [athlete.isArchived, athleteId, handleArchiveAthlete]);

  return (
    <PageContent
      pageHeadingLink={`${Constants.routes.athletes.route}/${athleteId}`}
      pageHeading={t("edit_athlete.page_heading")}
    >
      <Grid container mt={2} sx={{ width: "100%" }}>
        <Grid size={{ sm: 1, md: 2, lg: 3, xl: 4 }} />
        <Grid size={{ xs: 12, sm: 10, md: 8, lg: 6, xl: 4 }}>
          <Mui5.Paper elevation={athlete.isArchived ? 0 : undefined}>
            <Box sx={{ p: 4, position: "relative" }}>
              {!Object.keys(athlete).length ? (
                <Loader />
              ) : (
                <>
                  <AthleteForm
                    enableReinitialize
                    initialValues={{
                      ...athlete,
                      profilePhotoName: avatar.src,
                      sex: athlete.sex || "",
                      /**
                       * @author Carden
                       * @property {athlete.height}
                       * @description Height is record
                       * in Metre in DB but need to
                       * convert to CM for display
                       * and editing purposes.
                       */
                      height: athlete.height ? allowMaxTwoDecimals(athlete.height * 100) : null,
                      weight: athlete.weight ? allowMaxTwoDecimals(athlete.weight) : null,
                      groups: groups.filter((groupOption) => groupIds.includes(groupOption.id)),
                    }}
                    groupOptions={groups}
                    submitText={t("global.save_changes_label")}
                    photoChangeButtonText={t("edit_athlete.update_photo_button")}
                    onSubmit={handleSubmit}
                  />
                  <Grid container spacing="8px" pt={0.5} sx={{ width: "100%" }}>
                    <Grid size={12} />
                    {athlete.isArchived ? null : (
                      <Tooltip
                        arrow
                        placement="top"
                        severity="warning"
                        title={disableArchiveButton && <AthleteArchiveDisabledAlert />}
                      >
                        <Grid size="grow">
                          <ArchiveAthleteAction athleteId={athleteId} />
                        </Grid>
                      </Tooltip>
                    )}
                    <CanDoAction action="athlete.delete">
                      <Grid size={6}>
                        <DeleteAthleteAction athleteId={athleteId} />
                      </Grid>
                    </CanDoAction>
                  </Grid>
                  <ArchivedAthleteAlert isArchived={athlete.isArchived} unarchiveAthlete={handleToggleArchived} />
                </>
              )}
            </Box>
          </Mui5.Paper>
        </Grid>
      </Grid>
      {loading && <Loader />}
    </PageContent>
  );
}

export default EditAthlete;
